import React, { useState, lazy, Suspense, useEffect, useCallback, useRef, useMemo } from 'react';
import { Space, Drawer, Popover, Input, Form, Modal, Row, Col, Divider, Dropdown, Menu, Button, message } from 'antd';
import {
  isEmpty as _isEmpty,
  trim as _trim,
  map as _map,
  isUndefined as _isUndefined,
  find as _find,
  filter as _filter,
  cloneDeep as _cloneDeep,
  maxBy as _maxBy,
  forEach as _forEach
} from 'lodash-es';
import { useDispatch, useSelector } from 'react-redux';
import { nanoid } from 'nanoid';
import moment from 'moment';
import HopperImage from '../images/hopper_emoji.webp';
import BetaText from '../images/beta.png';
import Icon from 'components/Icon/Icon';
import { mouseflowTrack } from 'pages/Dashboard/utility';
import HopperBody from './HopperBody';
import { onCloseModal, getFirstActiveMenu, shouldShowWorkspaceDashboard } from 'utilities/utilities_exports';
import { useTranslation } from 'react-i18next';
import { apiServices } from 'redux/utils/api_services';
import {
  retrieveAndDecompressFromLocalStorage,
  compressAndStoreInLocalStorage,
  getOnboardingQuestions,
  isOnboardingSession,
  getOnboardingConversations,
  getNextOnboardingConversation
} from '../utility/utility';
import {
  deleteDataFromApiAction,
  updatePartialDataFromApiAction
} from 'components/Atoms/GraasApiUtility/GraasApiUtility.action';
import { getCustomDashboardList } from 'redux/CustomDashboard/CustomDashboard.action';
import './GraasHopperV2.scss';
import { getHopprConversations, insertHopprConversations } from '../redux/H.action';
import { hopperConstants } from '../redux/H.constants';
import { useNavigate } from 'react-router-dom';
import SMResult from 'components/SMResult/SMResult';

const StoresTab = lazy(() =>
  import(/* webpackChunkName: 'StoresTab' */ '../../../components/Atoms/GraasStoreGroupPicker/StoresTab')
);

const actionItems = [
  {
    label: 'Rename',
    key: 'edit',
    icon: 'edit'
  },
  {
    label: 'Delete',
    key: 'delete',
    icon: 'delete'
  }
];

const GraasHopperV2 = props => {
  const { t } = useTranslation();
  const {
    showHopper = true,
    channelFilterProps,
    currencyData,
    actionRequiredChannel = [],
    isHomePage = false,
    isHeaderMenu = false,
    redirectFromHoppr
  } = props;
  const navigate = useNavigate();
  const hopperHistoryData = useSelector(state => state?.Hopper?.history);
  const availableMenus = useSelector(state => state?.Navigation?.order);
  const navigationUserMenus = useSelector(state => state?.Navigation?.userMenu);
  const { userData } = useSelector(state => state?.User);
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const hopperConversation = useRef(null);
  const onSocketClose = useRef(null);
  const onSocketStop = useRef(null);
  const onboardingConv = useRef(null);
  const scrollRef = useRef(null);
  // As discussed with Ajinkya - we are hiding channel filter temporarely, so handling based on this flag
  const isNeedToShowChannelFilter = false;

  const [isHopperFullScreen, setIsHopperFullScreen] = useState(true);
  const [isHopperChatOpen, setIsHopperChatOpen] = useState(false);
  const [isChannelPopoverOpen, setIsChannelPopoverOpen] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState('');
  const [connections, setConnections] = useState({});
  const [currentConv, setCurrentConv] = useState([]);

  const [editedSessionID, setEditedSessionID] = useState('');
  const [sessionLabel, setSessionLabel] = useState('');
  const [activeSessionCount, setActiveSessionCount] = useState(-1);
  const [isOnboardingActive, setIsOnboardingActive] = useState();
  const [isOnboardingFinished, setIsOnboardingFinished] = useState(false);
  const [isEligibleToTriggerOnboardingMsg, setIsEligibleToTriggerOnboardingMsg] = useState(false);

  const [isMinimized, setIsMinimized] = useState(
    retrieveAndDecompressFromLocalStorage(`hopperMinimize-${currencyData?.merchantID}`) || false
  );

  //! states for custom typed queries
  const [customChatQuery, setCustomChatQuery] = useState('');
  const [searchPayload, setSearchPayload] = useState('');

  const [selectedChannels, setSelectedChannels] = useState([]);

  const [isLoadingResponse, setIsLoadingResponse] = useState(false);
  const [isQuestionLoading, setIsQuestionLoading] = useState(false);
  const [isHopprInitiated, setIsHopprInitiated] = useState(localStorage.getItem('isHopprInitiated') || false);
  const [dashboardMap, setDashboardMap] = useState({});
  const hasWriteAccess = useSelector(state => state?.Account?.hasWriteAccess);
  const WorkSpaceState = useSelector(state => state.WorkSpace);
  const hasDemoBanner = !hasWriteAccess;

  //! use :  Disable the input fields if the data check fails. Enable them only after confirmation
  const [isDataCheckPassed, setIsDataCheckPassed] = useState(
    _isEmpty(retrieveAndDecompressFromLocalStorage(`hopperTempHistory-${currencyData?.merchantID}`) || true)
  );

  useEffect(() => {
    dispatch(getHopprConversations());
  }, []);

  useEffect(() => {
    const filteredDashboards =
      _filter(WorkSpaceState?.activeDashboards, dashboard =>
        shouldShowWorkspaceDashboard(dashboard?.code, userData, WorkSpaceState)
      ) || [];
    const map = {};
    _forEach(filteredDashboards, dashboard => {
      if (_isUndefined(dashboard?.status) || dashboard?.status !== 'inactive') {
        map[dashboard?.code] = dashboard?.dashboardId;
      }
    });
    setDashboardMap(map);
  }, [WorkSpaceState?.activeDashboards]);

  const onboardingQuestions = useMemo(() => {
    return getOnboardingQuestions();
  }, []);
  const onboardingConversations = useMemo(() => {
    return getOnboardingConversations();
  }, []);

  useEffect(() => {
    if (_isEmpty(activeSessionId)) {
      if (isHopprInitiated) {
        let session_id = hopperHistoryData?.sessionMap?.[0]?.sessionIds?.[0] || '';
        setActiveSessionId(session_id);
        setCurrentConv(hopperHistoryData?.conversationMap?.[session_id] || []);
      } else {
        loadNewChatWindow();
      }
    }
  }, [hopperHistoryData, isHopprInitiated]);

  useEffect(() => {
    if (
      !_isUndefined(hopperHistoryData?.loading) &&
      !_isUndefined(hopperHistoryData?.error) &&
      !hopperHistoryData?.loading &&
      !hopperHistoryData?.error
    ) {
      const sessions = hopperHistoryData?.sessionMap || [];
      let count = 0;
      _map(sessions, session => {
        count += session?.sessionIds?.length || 0;
      });
      let session_id = activeSessionId || hopperHistoryData?.sessionMap?.[0]?.sessionIds?.[0] || '';
      let isOnboarding = isOnboardingSession(hopperHistoryData, session_id);
      if (_isEmpty(activeSessionId)) {
        isOnboarding = false;
      }
      setActiveSessionCount(count);
      setIsOnboardingActive(isOnboarding || count === 0);
      setIsHopprInitiated(isOnboarding || count <= 0);
    }
  }, [hopperHistoryData, activeSessionId]);

  useEffect(() => {
    if (
      (!_isUndefined(isOnboardingActive) && !isOnboardingActive) ||
      activeSessionCount > 1 ||
      currentConv?.length >= 6
    ) {
      setIsOnboardingFinished(true);
    } else {
      setIsOnboardingFinished(false);
    }
  }, [activeSessionCount, isOnboardingActive, currentConv]);

  useEffect(() => {
    if (
      !isOnboardingFinished &&
      (_isUndefined(WorkSpaceState?.initialDashboardList?.loading) ||
        (!_isUndefined(WorkSpaceState?.initialDashboardList?.error) && WorkSpaceState?.initialDashboardList?.error))
    ) {
      dispatch(
        getCustomDashboardList({
          trace_id: nanoid(),
          path: '/hoppr'
        })
      );
    }
  }, [dispatch, isOnboardingFinished]);

  useEffect(() => {
    if (isOnboardingActive && activeSessionCount === 0) {
      setIsEligibleToTriggerOnboardingMsg(true);
    }
  }, [activeSessionCount, isOnboardingActive]);

  useEffect(() => {
    if (isOnboardingActive && isEligibleToTriggerOnboardingMsg) {
      let maxObject = _maxBy(currentConv, 'conversation_id');
      const conversation_id = maxObject ? maxObject.conversation_id + 1 : 1;
      if (onboardingConv.current) {
        const nextConvObj = getNextOnboardingConversation(
          onboardingConversations,
          conversation_id,
          userData,
          currentConv,
          setCurrentConv,
          activeSessionId,
          dashboardMap
        );
        if (!_isEmpty(nextConvObj)) {
          // ! initiate hopper onboarding conversation
          setIsEligibleToTriggerOnboardingMsg(false);
          insertHopprConversations(nextConvObj);
          onboardingConv.current.updateChatObject(nextConvObj);
        }
      }
    }
  }, [isOnboardingActive, currentConv, isEligibleToTriggerOnboardingMsg]);

  useEffect(() => {
    if (window.location.pathname === '/hoppr') {
      window.localStorage.removeItem('redirectFromHoppr');
      setIsMinimized(false);
      compressAndStoreInLocalStorage(`hopperMinimize-${currencyData?.merchantID}`, false);
    }
  }, [window.location.pathname]);

  useEffect(() => {
    if (redirectFromHoppr === 'drawer') {
      setIsHopperFullScreen(false);
      setIsHopperChatOpen(true);
    } else if (redirectFromHoppr === 'icon') {
      setIsMinimized(true);
    }
  }, [redirectFromHoppr]);

  useEffect(() => {
    setSelectedChannels(channelFilterProps?.selectedChannels);
  }, [channelFilterProps?.selectedChannels]);

  // ! Set value to input box on change
  useEffect(() => {
    form.setFieldsValue({ hopperQuery: searchPayload });
  }, [searchPayload, form, customChatQuery]);

  // ! on click of ask button - submit form
  const onSendMessage = useCallback(
    payload => {
      if (!_isEmpty(searchPayload)) {
        setCustomChatQuery(searchPayload);
      }
      if (_isEmpty(searchPayload) && !_isEmpty(payload)) {
        setCustomChatQuery(payload);
      }
      setSearchPayload('');
      if (!isHopprInitiated) {
        localStorage.setItem('isHopprInitiated', true);
        setIsHopprInitiated(true);
      }
    },
    [searchPayload]
  );

  // ✅ Ensure `onStartHopperConversation` is triggered **after** state updates
  useEffect(() => {
    if (hopperConversation.current && !_isEmpty(customChatQuery)) {
      // ! initiate hopper new conversation on click of ask button
      hopperConversation.current.onStartHopperConversation();
    }
  }, [customChatQuery]);

  // ! On close sidebar
  const onCloseSidebar = () => {
    setCustomChatQuery('');
    setSearchPayload('');
    handledMinimize(false);
    updateQueryParameters(false);
    // ! initiate hopper new conversation on click of ask button
    if (onSocketClose.current) onSocketClose.current.onCloseSocketConnection();
    window.localStorage.removeItem('redirectFromHoppr');
  };

  // * Scroll to bottom on initiate new message to see latest message
  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth' // Optional: smooth scrolling
      });
    }
  };

  const isMinimizedRequired = isRequired => {
    if (!isMinimized) return;
    if (isRequired) {
      document.getElementsByClassName('hopperButton')[0].classList.add('minimized');
    } else {
      document.getElementsByClassName('hopperButton')[0].className = 'hopperButton';
    }
  };

  // * handled minimize logic here
  const handledMinimize = value => {
    compressAndStoreInLocalStorage(`hopperMinimize-${currencyData?.merchantID}`, value);
    setIsHopperChatOpen(false);
    setIsHopperFullScreen(false);
    updateQueryParameters(false);
    isMinimizedRequired(value);
    setIsMinimized(value);
    window.localStorage.setItem('redirectFromHoppr', 'icon');
    if (isHopperFullScreen) redirectToMenu();
  };

  const getSessionTitle = sessionId => {
    if (!_isEmpty(hopperHistoryData?.sessionDetailMap?.[sessionId]?.label)) {
      return hopperHistoryData?.sessionDetailMap?.[sessionId]?.label;
    }
    let conv = hopperHistoryData?.conversationMap?.[sessionId];
    if (conv?.[0].isOnboardingQuestion) {
      return onboardingQuestions?.[0];
    }
    return conv?.[0]?.label || conv?.[0]?.question_text || 'N/A';
  };

  const updateSessionMap = (sessions, sessionObj, sessionId) => {
    const sessionDetailMap = hopperHistoryData?.sessionDetailMap || {};
    const activeSessionIds =
      _filter(sessionObj?.sessionIds, id => {
        return (
          (_isUndefined(sessionDetailMap?.[id]?.status) || sessionDetailMap?.[id]?.status !== 'X') && id !== sessionId
        );
      }) || [];
    if (_isEmpty(activeSessionIds)) {
      const updatedSessionMap = _filter(sessions, session => session?.date !== sessionObj?.date) || [];
      dispatch({
        type: hopperConstants.GET_HOPPER_SESSION_UPDATE,
        payload: { sessionMap: updatedSessionMap }
      });
    }
  };

  const deleteConversation = async (sessionId, activeSessionId, conversationId, isExistingConvAvailable) => {
    const apiUrl = `${apiServices['hoppr']}/services/conversation`;
    let data = {
      session_id: sessionId
    };
    if (!_isEmpty(conversationId)) {
      data.conversation_id = conversationId;
      data.update_status = 'deleted';
    }
    let apiResponse = await deleteDataFromApiAction(apiUrl, {
      data: data
    });
    if (apiResponse?.hasError) {
      if (_isEmpty(conversationId)) {
        message.error(t('GRAAS_SESSION_DELETED_FAILED_MESSAGE_GRAAS'));
      }
    } else {
      if (_isEmpty(conversationId)) {
        message.success(t('GRAAS_SESSION_DELETED_SUCCESS_MESSAGE_GRAAS'));
      }
      if (isExistingConvAvailable && _isEmpty(conversationId)) {
        dispatch({
          type: hopperConstants.UPDATE_PATCH_HOPPER_SESSION_DETAILS,
          payload: { session_id: sessionId, data: { status: 'X' } }
        });
        const sessions = hopperHistoryData?.sessionMap || [];
        const sessionObj = _find(sessions, item => item.sessionIds.includes(sessionId));
        updateSessionMap(sessions, sessionObj, sessionId);
      } else if (!isExistingConvAvailable && !_isEmpty(conversationId)) {
        const current_date = `${moment().format('YYYY-MM-DD')}`;
        const sessions = hopperHistoryData?.sessionMap || [];
        const sessionObj = _find(sessions, session => session?.date === current_date) || {};
        updateSessionMap(sessions, sessionObj, activeSessionId);
      }
      if (_isEmpty(conversationId) || !isExistingConvAvailable) {
        if (!isExistingConvAvailable && sessionId === activeSessionId) {
          loadNewChatWindow();
        } else {
          setActiveSessionId('');
        }
      }
    }
  };

  const handledOnClick = async (type, sessionId, activeSessionId) => {
    if (type == 'delete') {
      await deleteConversation(sessionId, activeSessionId, '', true);
    } else {
      setEditedSessionID(sessionId);
    }
  };

  const updateSessionLabel = useCallback(async () => {
    try {
      if (_isEmpty(sessionLabel)) {
        setEditedSessionID('');
        setSessionLabel('');
        return;
      }
      const apiUrl = `${apiServices['hoppr']}/services/conversation`;
      let apiResponse = await updatePartialDataFromApiAction(apiUrl, {
        session_id: editedSessionID,
        label: sessionLabel
      });
      if (apiResponse?.hasError) {
        message.error(t('GRAAS_SESSION_LABEL_UPDATE_FAILED_MESSAGE_GRAAS'));
      } else {
        dispatch({
          type: hopperConstants.UPDATE_PATCH_HOPPER_SESSION_DETAILS,
          payload: { session_id: editedSessionID, data: { label: sessionLabel } }
        });
        setEditedSessionID('');
        setSessionLabel('');
        message.success(t('GRAAS_SESSION_RENAMED_SUCCESS_MESSAGE_GRAAS'));
      }
    } catch (error) {
      message.error(t('GRAAS_SESSION_LABEL_UPDATE_FAILED_MESSAGE_GRAAS'));
    }
  }, [editedSessionID, sessionLabel]);

  const renderChatConvHistories = () => {
    if (_isUndefined(hopperHistoryData?.loading) || hopperHistoryData?.loading) {
      return (
        <>
          <div className='layout no-data'>
            <SMResult type='loader' message={t('GRAAS_LOADING_GRAAS')} />
          </div>
        </>
      );
    }
    if (!isOnboardingFinished) {
      return (
        <div className='layout no-data'>
          <div className='title'>{`Welcome to Hoppr!`}</div>
          <div className='message'>{`Complete the guided introduction to start exploring your e-commerce data.`}</div>
        </div>
      );
    }
    return (
      <>
        <div className={`layout ${isHeaderMenu ? (hasDemoBanner ? 'demobanner' : 'page') : ''}`}>
          {_map(hopperHistoryData?.sessionMap, session => {
            return (
              <>
                <div className='section-heading'>{session?.date}</div>
                <ul className='conv-title'>
                  {!_isEmpty(session?.sessionIds) &&
                    _map(session?.sessionIds, sessionId => {
                      if (
                        hopperHistoryData?.sessionDetailMap?.[sessionId]?.status === 'X' ||
                        _isEmpty(
                          _find(
                            hopperHistoryData?.conversationMap?.[sessionId] || [],
                            conv => _isUndefined(conv?.update_status) && !_isUndefined(conv?.question_text)
                          ) || {}
                        )
                      ) {
                        return <></>;
                      }
                      return (
                        <li key={sessionId} className={`${sessionId === activeSessionId ? 'active' : ''}`}>
                          <button
                            className={`tab`}
                            style={{ cursor: isLoadingResponse || isQuestionLoading ? 'no-drop' : 'pointer' }}
                            onClick={() => {
                              if (!isLoadingResponse && !isQuestionLoading) {
                                setActiveSessionId(sessionId);
                                setCurrentConv(hopperHistoryData?.conversationMap?.[sessionId] || []);
                                if (!isHopprInitiated) {
                                  localStorage.setItem('isHopprInitiated', true);
                                  setIsHopprInitiated(true);
                                }
                              }
                            }}
                          >
                            {editedSessionID !== sessionId ? (
                              <>
                                <span className='tab_title'>{getSessionTitle(sessionId)}</span>
                              </>
                            ) : (
                              <>
                                <Input
                                  className='Graas-textbox'
                                  placeholder='Enter the label'
                                  defaultValue={getSessionTitle(sessionId)}
                                  onChange={e => setSessionLabel(e.target.value)}
                                />
                              </>
                            )}
                          </button>
                          {editedSessionID !== sessionId ? (
                            <>
                              <Dropdown
                                overlay={
                                  <div className='customSelect_menuWrapper' style={{ minWidth: 120 }}>
                                    <Menu
                                      items={_map(actionItems, (options, index) => {
                                        const { label, key, icon } = options;
                                        return {
                                          label: (
                                            <Button
                                              className='customSelect_menu'
                                              style={{ padding: '8px 12px 8px 12px' }}
                                              onClick={() => handledOnClick(key, sessionId, activeSessionId)}
                                            >
                                              <Icon name={icon} size={'sm'} />
                                              <span className='Graas-heading size-6'>{label}</span>
                                            </Button>
                                          ),
                                          key: key
                                        };
                                      })}
                                    />
                                  </div>
                                }
                                placement={'bottomRight'}
                                overlayClassName='dropdown_overlay'
                              >
                                <button className='Graas-settingButton is-clean small hover-effect'>
                                  <Icon name='more_vert' />
                                </button>
                              </Dropdown>
                            </>
                          ) : (
                            <>
                              <div style={{ marginRight: '8px' }}>
                                <Space>
                                  <button
                                    className='Graas-settingButton is-clean extra-small'
                                    style={{ width: '17px' }}
                                    onClick={() => {
                                      updateSessionLabel();
                                    }}
                                  >
                                    <Icon shade='success' name='done' color='#149b5c' />
                                  </button>
                                  <button
                                    className='Graas-settingButton is-clean extra-small'
                                    onClick={() => {
                                      setSessionLabel('');
                                      setEditedSessionID('');
                                    }}
                                    style={{ width: '17px' }}
                                  >
                                    <Icon shade='type-alert' name='close' isBordered />
                                  </button>
                                </Space>
                              </div>
                            </>
                          )}
                        </li>
                      );
                    })}
                </ul>
              </>
            );
          })}
        </div>
      </>
    );
  };

  const loadNewChatWindow = () => {
    setCurrentConv([]);
    setActiveSessionId('');
  };

  const renderHopperHistory = () => {
    return (
      <>
        <div
          className={`hopperChatHistoryV2_header`}
          style={{
            marginTop: hasDemoBanner ? 60 : 0
          }}
        >
          <Row align='middle' className='title'>{`Chat history`}</Row>
        </div>
        <Divider style={{ backgroundColor: '#D9E2F0', margin: '0' }} />
        {renderChatConvHistories()}
        <div className={`hopperChatHistoryV2_footer`}>
          <Row align='middle' className='title'>
            <button
              className='Graas-button is-bordered is-fullWidth'
              style={{
                cursor: !isOnboardingFinished || isLoadingResponse || isQuestionLoading ? 'no-drop' : 'pointer'
              }}
              onClick={() => {
                if (!isLoadingResponse && !isQuestionLoading && isOnboardingFinished) {
                  loadNewChatWindow();
                }
              }}
            >
              <Space>
                {`New chat`}
                <Icon name='add' />
              </Space>
            </button>
          </Row>
        </div>
      </>
    );
  };

  const renderHopperLayout = () => {
    return (
      <>
        <div className={`${isHeaderMenu ? 'page' : ''}`}>
          <Row>
            <Col md={6} className='history'>
              {renderHopperHistory()}
            </Col>
            <Col md={18} style={{ backgroundColor: '#fff' }}>
              {renderHopperInstance()}
            </Col>
          </Row>
        </div>
      </>
    );
  };

  const renderMessageInputLayout = () => {
    return (
      <>
        <Form name='hopperChatBot' onFinish={onSendMessage} autoComplete='off' form={form} className='chatBotForm'>
          <div className='inputSection'>
            <Form.Item name='hopperQuery' className='mb-0'>
              <Input
                className='c-textbox width-100 mb-0'
                onChange={e => setSearchPayload(e.target.value)}
                placeholder={'Type your message here...'}
                disabled={isLoadingResponse || !isOnboardingFinished || isQuestionLoading || !isDataCheckPassed}
              />
            </Form.Item>
          </div>
          <div className='buttonSection'>
            {!isLoadingResponse && !isQuestionLoading && (
              <button
                className='c-button is-filled'
                disabled={
                  isLoadingResponse || !isOnboardingFinished || _isEmpty(searchPayload) || _trim(searchPayload) === ''
                }
              >
                Ask
              </button>
            )}

            {(isLoadingResponse || isQuestionLoading) && (
              <button
                className='c-button is-filled'
                type='button'
                onClick={() => {
                  if (onSocketStop.current) onSocketStop.current.onStopSocketConnection();
                }}
              >
                <Space>
                  <Icon name='stop_circle' size='small' />
                  Stop
                </Space>
              </button>
            )}
          </div>
        </Form>
      </>
    );
  };

  const redirectToMenu = () => {
    navigate(getFirstActiveMenu(navigationUserMenus, availableMenus));
  };

  const openHopprDrawer = isNavigateRequired => {
    setIsHopperFullScreen(false);
    setIsHopperChatOpen(true);
    window.localStorage.setItem('redirectFromHoppr', 'drawer');
    if (isNavigateRequired) redirectToMenu();
  };

  // ! Render hopper UI in modal and drawer
  const renderHopperInstance = () => {
    return (
      <>
        <div
          className={`hopperChatBodyV2_header filled`}
          style={{
            marginTop: hasDemoBanner ? 60 : 0
          }}
        >
          <Row align='middle'>
            <Col md={12}>
              <Space size={12}>
                <div className='hopperEmoji'></div>
                <div>
                  <span className='c-heading smm mb-0 color-white main_header'>Hi! I’m Hoppr!</span>
                  <span className='c-heading sm mb-0 color-white bold'>Your data expert friend</span>
                </div>
              </Space>
            </Col>
            <Col md={12} className='text-right'>
              <Space size={6}>
                {isNeedToShowChannelFilter && (
                  <Popover
                    open={isChannelPopoverOpen}
                    placement='bottomLeft'
                    title={null}
                    onOpenChange={e => {
                      setIsChannelPopoverOpen(e);
                    }}
                    destroyTooltipOnHide
                    overlayClassName='c-customPopover position-fixed'
                    overlayInnerStyle={{ width: '800px' }}
                    content={
                      <Suspense fallback={<></>}>
                        <StoresTab
                          initialChannels={channelFilterProps?.initialChannels}
                          selectedChannels={selectedChannels}
                          allChannels={channelFilterProps?.allChannels}
                          selectedChannelGroup={[]}
                          channelGroup={[]}
                          onCancel={() => setIsChannelPopoverOpen(false)}
                          onSelectChannels={e => {
                            setSelectedChannels(e.value);
                            setIsChannelPopoverOpen(false);
                          }}
                          isChannelEnabledFlag={false}
                          isInactiveChannelEnabled={false}
                          isChannelCurrencyEnabled={false}
                        />
                      </Suspense>
                    }
                    trigger={isDataCheckPassed ? 'click' : []}
                  >
                    <button className='c-button is-bordered white sm' disabled={!isDataCheckPassed}>
                      <span className='filterValue capitalize'>
                        <Space>
                          <Icon name='filter_list' size='default' />
                          {selectedChannels?.length === channelFilterProps?.selectedChannels?.length
                            ? 'All Channels'
                            : `${selectedChannels?.length} Channels`}
                          <Icon name='expand_more' size='default' />
                        </Space>
                      </span>
                    </button>
                  </Popover>
                )}
                <button
                  className='settingButton no-rounded clean sm'
                  onClick={() => {
                    if (isHopperFullScreen) {
                      openHopprDrawer(true);
                    } else {
                      setIsHopperFullScreen(true);
                      setIsHopperChatOpen(false);
                      navigate('/hoppr');
                    }
                  }}
                >
                  <Icon
                    name={isHopperFullScreen ? 'close_fullscreen' : 'open_in_full'}
                    size='default'
                    color='#212227'
                  />
                </button>
                <button className='settingButton no-rounded clean sm' onClick={() => handledMinimize(true)}>
                  <Icon name='minimize' size='default' color='#212227' />
                </button>
                {!isHopperFullScreen && (
                  <>
                    <button className='settingButton no-rounded clean sm' onClick={() => onCloseSidebar()}>
                      <Icon name='close' size='default' color='#212227' />
                    </button>
                  </>
                )}
              </Space>
            </Col>
          </Row>
        </div>
        <div
          className={`hopperChatBodyV2 ${isHeaderMenu ? (hasDemoBanner ? 'demobanner' : 'page') : ''}`}
          ref={scrollRef}
        >
          <HopperBody
            channelFilterProps={channelFilterProps}
            selectedChannels={selectedChannels}
            customChatQuery={customChatQuery}
            currencyData={currencyData}
            setSearchPayload={setSearchPayload}
            isLoadingResponse={isLoadingResponse}
            setIsLoadingResponse={setIsLoadingResponse}
            ref={[hopperConversation, onSocketClose, onSocketStop, onboardingConv]}
            onSendMessage={onSendMessage}
            setCustomChatQuery={setCustomChatQuery}
            scrollToBottom={scrollToBottom}
            setIsQuestionLoading={setIsQuestionLoading}
            isQuestionLoading={isQuestionLoading}
            setIsDataCheckPassed={setIsDataCheckPassed}
            actionRequiredChannel={actionRequiredChannel}
            renderMessageInputLayout={renderMessageInputLayout}
            currentConv={currentConv}
            setCurrentConv={setCurrentConv}
            activeSessionId={activeSessionId}
            setActiveSessionId={setActiveSessionId}
            connections={connections}
            setConnections={setConnections}
            deleteConversation={deleteConversation}
            isNeedToShowChannelFilter={isNeedToShowChannelFilter}
            isOnboardingActive={isOnboardingActive}
            setIsEligibleToTriggerOnboardingMsg={setIsEligibleToTriggerOnboardingMsg}
            openHopprDrawer={openHopprDrawer}
            dashboardMap={dashboardMap}
          />
        </div>
        {!_isEmpty(currentConv) && <div className='hopperChatBodyV2_footer v-bottom'>{renderMessageInputLayout()}</div>}
      </>
    );
  };

  const onCloseFullScreen = () => {
    onCloseModal();
    handledMinimize(false);
    updateQueryParameters(false);
    // ! initiate hopper new conversation on click of ask button
    if (onSocketClose.current) onSocketClose.current.onCloseSocketConnection();
  };

  const updateQueryParameters = isAddQueryParams => {
    const params = new URLSearchParams(window.location.search);
    if (isAddQueryParams) {
      params.set('subTab', 'hopper');
    } else {
      params.delete('subTab');
    }
    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.replaceState({}, '', newUrl);
  };

  if (!showHopper) return null;

  return (
    <>
      {!isHomePage && isMinimized && (
        <button
          className={`hopperButton ${isMinimized ? 'minimized' : ''} `}
          onClick={() => {
            setIsHopperFullScreen(false);
            setIsHopperChatOpen(true);
            updateQueryParameters(true);
            mouseflowTrack(
              {
                username: currencyData?.userName || '',
                email: currencyData?.email || '',
                storeCode: currencyData?.merchantID || '',
                isDemoAccount: !hasWriteAccess
              },
              'HopperChatOpened'
            );
          }}
        >
          <img src={HopperImage} alt='hopper' className='hoppr-image' />
          <img src={BetaText} alt='beta' className='beta-badge' />
        </button>
      )}

      {/* ***** Drawer ***** */}
      {!isHopperFullScreen && isHopperChatOpen && (
        <Drawer
          title={null}
          placement='right'
          open={isHopperChatOpen}
          width={620}
          className='hopperChatContainer-v2'
          closeIcon={null}
        >
          {renderHopperInstance()}
        </Drawer>
      )}

      {/* ***** Page ***** */}
      {isHeaderMenu && renderHopperLayout()}
    </>
  );
};

export default GraasHopperV2;
